import React, { useState, useRef, useCallback } from 'react';
import { useAuthContext } from '../../../contexts/AuthContext';
import { sendPasswordReset } from '../../../services/authService';
import './molecules.css';

interface FloatingChar { char: string; x: number; y: number; id: number; }
let _lsCharId = 0;

interface LoginScreenProps {
  onContinue: () => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onContinue }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);
  const [floatingChars, setFloatingChars] = useState<FloatingChar[]>([]);
  const prevLenRef = useRef<Record<string, number>>({});

  const { signIn, signUp, signInGoogle, error: authError } = useAuthContext();
  const error = localError || authError;

  const spawnFloat = useCallback((e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const val = e.target.value;
    const newLen = val.length;
    const oldLen = prevLenRef.current[field] ?? 0;
    prevLenRef.current[field] = newLen;
    if (newLen <= oldLen) return;
    const lastChar = val.charAt(newLen - 1);
    const rect = e.target.getBoundingClientRect();
    const id = ++_lsCharId;
    setFloatingChars(prev => [...prev, { char: lastChar, x: rect.left + Math.min(newLen * 9, rect.width - 20), y: rect.top - 8, id }]);
    setTimeout(() => setFloatingChars(prev => prev.filter(c => c.id !== id)), 400);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    if (isSignUp && !displayName.trim()) {
      setLocalError('Please enter your name');
      return;
    }

    setLocalError(null);
    setLoading(true);

    try {
      if (isSignUp) {
        await signUp(email, password, displayName, 'participant');
      } else {
        await signIn(email, password);
      }
      onContinue();
    } catch {
      // Error is surfaced via authError
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const target = email.trim();
    if (!target) {
      setLocalError('Enter your email above first, then tap Forgot password.');
      return;
    }
    setLocalError(null);
    setLoading(true);
    try {
      await sendPasswordReset(target);
      setResetSentTo(target);
    } catch (err: any) {
      setLocalError(err.message || 'Could not send the reset link.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLocalError(null);
    setLoading(true);

    try {
      await signInGoogle('participant');
      onContinue();
    } catch {
      // Error is surfaced via authError
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#D4715A] min-h-screen relative overflow-hidden flex justify-center">
      <div className="relative w-full max-w-[420px] min-h-screen md:flex md:flex-col md:justify-center">
      {/* Molecular decorations */}

      {/* Top left - 4-node constellation */}
      <div className="mol" style={{position:'absolute',top:55,left:50,pointerEvents:'none'}}>
        <svg width="90" height="85" viewBox="-35 -35 130 120">
          <circle cx="20" cy="16" r="5" fill="#fff"/>
          <circle cx="50" cy="8" r="3" fill="#fff"/>
          <circle cx="65" cy="32" r="4" fill="#fff"/>
          <circle cx="35" cy="50" r="3" fill="#fff"/>
          <line x1="20" y1="16" x2="50" y2="8" stroke="#fff" strokeWidth="2"/>
          <line x1="50" y1="8" x2="65" y2="32" stroke="#fff" strokeWidth="2"/>
          <line x1="20" y1="16" x2="35" y2="50" stroke="#fff" strokeWidth="2"/>
          {/* UP-LEFT ↖ from top-left node */}
          <path d="M16,12 Q6,2 -4,12 Q-14,2 -24,12 Q-34,2 -44,12" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
          {/* DOWN-RIGHT ↘ from right node */}
          <path d="M68,35 Q78,45 88,35 Q98,45 108,35" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Top right - 2-node vertical */}
      <div className="mol-d1" style={{position:'absolute',top:130,right:50,pointerEvents:'none'}}>
        <svg width="60" height="70" viewBox="-10 -35 100 140">
          <circle cx="25" cy="12" r="4" fill="#fff"/>
          <circle cx="25" cy="52" r="4" fill="#fff"/>
          <line x1="25" y1="12" x2="25" y2="52" stroke="#fff" strokeWidth="2"/>
          {/* UP-RIGHT ↗ from top node (2 ripples) */}
          <path d="M28,9 Q38,-1 48,9 Q58,-1 68,9" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
          {/* DOWN-RIGHT ↘ from bottom node (3 ripples) */}
          <path d="M28,55 Q38,65 48,55 Q58,65 68,55 Q78,65 88,55" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Bottom left - 2-node horizontal (staggered lower-left) */}
      <div className="mol-d2" style={{position:'absolute',bottom:160,left:30,pointerEvents:'none'}}>
        <svg width="75" height="55" viewBox="-35 -30 145 115">
          <circle cx="15" cy="25" r="4" fill="#fff"/>
          <circle cx="55" cy="25" r="4" fill="#fff"/>
          <line x1="15" y1="25" x2="55" y2="25" stroke="#fff" strokeWidth="2"/>
          {/* DOWN-LEFT ↙ from left node */}
          <path d="M12,28 Q2,38 -8,28 Q-18,38 -28,28 Q-38,38 -48,28" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
          {/* UP-RIGHT ↗ from right node */}
          <path d="M58,22 Q68,12 78,22 Q88,12 98,22" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Bottom right - 3-node zigzag (staggered higher-right) */}
      <div className="mol-d3" style={{position:'absolute',bottom:240,right:40,pointerEvents:'none'}}>
        <svg width="70" height="70" viewBox="-35 -30 135 130">
          <circle cx="15" cy="10" r="3" fill="#fff"/>
          <circle cx="40" cy="35" r="4" fill="#fff"/>
          <circle cx="65" cy="60" r="3" fill="#fff"/>
          <line x1="15" y1="10" x2="40" y2="35" stroke="#fff" strokeWidth="2"/>
          <line x1="40" y1="35" x2="65" y2="60" stroke="#fff" strokeWidth="2"/>
          {/* UP-LEFT ↖ from top node */}
          <path d="M12,7 Q2,-3 -8,7 Q-18,-3 -28,7" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
          {/* DOWN-RIGHT ↘ from bottom node */}
          <path d="M68,63 Q78,73 88,63 Q98,73 108,63" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 mt-[220px] md:mt-0 px-7">
        {/* Branding */}
        <span className="text-white text-[15px] font-semibold tracking-[0.5px]">Dekko</span>
        <div className="mt-1">
          <span className="text-white/60 text-[11px] font-semibold tracking-[2px]">RESEARCH PLATFORM</span>
        </div>

        {/* Form fields */}
        <form className="mt-7" onSubmit={handleSubmit}>
          {isSignUp && (
            <div className="bg-white/95 rounded-[14px] p-4 mb-3">
              <label className="block text-[#D4715A] text-[11px] font-semibold tracking-[1px] mb-1.5">NAME</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); spawnFloat(e, 'name'); }}
                className="w-full bg-transparent text-[#1A1814] text-base outline-none placeholder:text-[#1A1814]/40"
                placeholder="Your name"
              />
            </div>
          )}

          {/* Email */}
          <div className="bg-white/95 rounded-[14px] p-4 mb-3">
            <label className="block text-[#D4715A] text-[11px] font-semibold tracking-[1px] mb-1.5">EMAIL</label>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setResetSentTo(null); spawnFloat(e, 'email'); }}
              className="w-full bg-transparent text-[#1A1814] text-base outline-none placeholder:text-[#1A1814]/40"
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>

          {/* Password */}
          <div className="bg-white/95 rounded-[14px] p-4 flex items-center justify-between">
            <div className="flex-1">
              <label className="block text-[#D4715A] text-[11px] font-semibold tracking-[1px] mb-1.5">PASSWORD</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); spawnFloat(e, 'password'); }}
                className="w-full bg-transparent text-[#1A1814] text-base outline-none placeholder:text-[#1A1814]/40"
                placeholder="••••••••"
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
              />
            </div>
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="ml-3"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                {showPassword ? (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" stroke="#1A1814" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" stroke="#1A1814" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
                    <line x1="1" y1="1" x2="23" y2="23" stroke="#1A1814" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
                  </>
                ) : (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#1A1814" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
                    <circle cx="12" cy="12" r="3" stroke="#1A1814" strokeWidth="1.5" opacity="0.4"/>
                  </>
                )}
              </svg>
            </button>
          </div>

          {/* Forgot password */}
          {!isSignUp && (
            <div className="mt-2.5 text-right">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={loading}
                className="text-white/70 text-[13px] underline underline-offset-2 disabled:opacity-50"
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-3 px-5 py-3 bg-white/20 rounded-xl text-white text-sm text-center leading-snug">
              {error}
            </div>
          )}

          {/* Enumeration-safe: Firebase resolves identically for unknown
              addresses, so this must not assert the account exists. */}
          {resetSentTo && !error && (
            <div className="mt-3 px-5 py-3 bg-white/20 rounded-xl text-white text-sm text-center leading-snug">
              If an account exists for {resetSentTo}, a reset link is on its way. Check your spam folder too.
            </div>
          )}

          {/* Continue button */}
          <button
            type="submit"
            disabled={loading || !email.trim() || !password.trim()}
            className="w-full mt-6 bg-[#1A1814] text-white rounded-[28px] py-[18px] text-[15px] font-semibold tracking-[1px] flex items-center justify-center gap-2.5 disabled:opacity-50"
          >
            {loading ? 'Please wait...' : 'CONTINUE'}
            {!loading && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M13 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-4 my-5">
          <div className="flex-1 h-px bg-white/20"></div>
          <span className="text-white/50 text-xs">or</span>
          <div className="flex-1 h-px bg-white/20"></div>
        </div>

        {/* Google sign in */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full bg-white text-[#1A1814] rounded-[28px] py-4 text-[15px] font-semibold flex items-center justify-center gap-3 disabled:opacity-50"
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        {/* Footer toggle */}
        <p className="text-center mt-5 text-white/50 text-sm pb-10">
          {isSignUp ? (
            <>Already have an account?{' '}
              <button onClick={() => { setIsSignUp(false); setLocalError(null); }} className="text-white font-medium">Sign in</button>
            </>
          ) : (
            <>New participant?{' '}
              <button onClick={() => { setIsSignUp(true); setLocalError(null); }} className="text-white font-medium">Create account</button>
            </>
          )}
        </p>
      </div>

      </div>
      {floatingChars.map(fc => (
        <span key={fc.id} className="ks-float" style={{ left: fc.x, top: fc.y }}>{fc.char}</span>
      ))}
      <style>{`
        @keyframes ksUp { 0% { opacity:1; transform:scale(1) translateY(0); } 100% { opacity:0; transform:scale(0.5) translateY(-18px); } }
        .ks-float { position:fixed; z-index:50; pointer-events:none; font-size:32px; font-weight:600; color:#1A1814; animation:ksUp 0.35s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default LoginScreen;
